import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import type { DatePickerValue } from '@factorialco/f0-react/dist/experimental'
import { Form } from 'factorial-form'
import {
  BulkCreateWorkSchedulePlanningVersionsDocument,
  EmployeesEmployeeSelectorInput,
  WorkScheduleEmployeesDocument
} from 'generated/resources/graphql'
import moment from 'moment'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { MINIMUM_EFFECTIVE_AT } from 'modules/time_planning/lib/constants'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

interface AddEmployeesModalProps {
  workSchedule: WorkSchedule
  onClose: () => void
  onSuccess: () => void
}

const dateToDatePickerValue = (date: Date): DatePickerValue => {
  return {
    granularity: 'day',
    value: {
      from: date,
      to: date
    }
  }
}

const AddEmployeesModal = ({ workSchedule, onClose, onSuccess }: AddEmployeesModalProps) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: []
  })
  const [dateError, setDateError] = useState<string | undefined>(undefined)

  // get employees already assigned to this work schedule

  const form = useLazyRef<Form>(
    () =>
      new Form(
        {
          effective_at: i18nDateToString(moment(), 'backend')
        },
        {
          effective_at: 'date'
        }
      )
  ).current

  const [bulkCreateWorkSchedulePlanningVersions, { loading: _loading }] = useMutation(
    BulkCreateWorkSchedulePlanningVersionsDocument,
    {
      refetchQueries: [WorkScheduleEmployeesDocument],
      extractorFn: (data) => data.timePlanningMutations.bulkCreateTimeplanningPlanningVersion
    }
  )

  const effectiveAtValue = form.get('effective_at')?.value

  const datePickerValue = useMemo((): DatePickerValue => {
    if (!effectiveAtValue) {
      return dateToDatePickerValue(new Date())
    }
    if (effectiveAtValue instanceof Date) {
      return dateToDatePickerValue(effectiveAtValue)
    }
    const parsedDate = stringToDate(effectiveAtValue, 'backend')
    if (!parsedDate.isValid()) {
      const parsedUiDate = stringToDate(effectiveAtValue, 'ui')
      if (!parsedUiDate.isValid()) {
        return dateToDatePickerValue(new Date())
      }
      return dateToDatePickerValue(parsedUiDate.toDate())
    }
    return dateToDatePickerValue(parsedDate.toDate())
  }, [effectiveAtValue])

  const onSubmit = async () => {
    const formValue = form.get('effective_at').value
    const effectiveDate =
      formValue instanceof Date
        ? formValue
        : stringToDate(formValue, 'backend').isValid()
        ? stringToDate(formValue, 'backend').toDate()
        : stringToDate(formValue, 'ui').toDate()

    if (effectiveDate < MINIMUM_EFFECTIVE_AT) {
      const errorMessage = i18n.t('time_planning.planning_versions.errors.effective_at_too_early')
      setDateError(errorMessage)
      app.setFlash({ type: 'wrong', message: errorMessage })
      return
    }

    setDateError(undefined)
    const response = await bulkCreateWorkSchedulePlanningVersions({
      variables: {
        effectiveAt: i18nDateToString(effectiveDate, 'backend'),
        scheduleId: workSchedule.attributes.id,
        employeeSelection: selectedEmployees
      }
    })
    const handled = handleResult(response, () => undefined)
    if (handled.success) {
      onSuccess()
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (_loading) return
        onClose()
      }}
      title={i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
      description={i18n.t('settings.work_schedule.employees_tab.employee_modal.description')}
      primaryAction={{
        label: i18n.t('settings.work_schedule.employees_tab.employee_modal.assign'),
        onClick: onSubmit,
        disabled: _loading || !!dateError,
        loading: _loading
      }}
      secondaryAction={{
        label: i18n.t('settings.work_schedule.cancel'),
        onClick: onClose
      }}
    >
      <>
        <EmployeeSelectorV2
          messages={{
            placeholder: i18n.t('common.select_users')
          }}
          forcedFilters={{
            excludeIds: workSchedule.employeeIds
          }}
          onChange={setSelectedEmployees}
          multiple
          options={{
            hideSearchIcon: true
          }}
        />

        <Box paddingY="s16">
          <F0DatePicker
            label={i18n.t('settings.work_schedule.employees_tab.table.header.date')}
            onChange={(date) => {
              if (!date?.value?.from) return
              setDateError(undefined)
              form.get('effective_at').set(date.value.from)
            }}
            size="md"
            minDate={MINIMUM_EFFECTIVE_AT}
            error={dateError}
            value={datePickerValue}
            hint={i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
          />
        </Box>
      </>
    </F0Dialog>
  )
}

export default AddEmployeesModal
